import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_NAME = 'Tinkerbots';
const DEFAULT_TITLE = 'Tinkerbots | AI, Coding & Robotics Lessons for Kids';
const TITLE_SUFFIX = ` | ${SITE_NAME}`;
const DEFAULT_DESCRIPTION =
  'Spark curiosity with personalized AI, coding, and robotics lessons for kids aged 5–15. Start a 7-day free trial for interactive STEM activities, games, and kid-safe AI tutor characters!';
const DEFAULT_IMAGE = '/logo.png';
const DEFAULT_SITE_URL = 'https://tinkerbots.studio';

type SeoConfig = {
  title: string;
  description: string;
  image?: string;
  canonicalPath?: string;
  robots?: string;
  jsonLd?: Record<string, unknown>;
};

const publicRouteSeo: Record<string, SeoConfig> = {
  '/': {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: '/',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: SITE_NAME,
      applicationCategory: 'EducationalApplication',
      operatingSystem: 'Web',
      audience: {
        '@type': 'EducationalAudience',
        educationalRole: 'student',
        audienceType: 'Children aged 5 to 15',
      },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'USD',
      },
    },
  },
  '/privacy': {
    title: `Privacy Policy & COPPA Compliance${TITLE_SUFFIX}`,
    description:
      'Read how Tinkerbots ensures a secure, COPPA-compliant educational space. We protect children\'s data privacy, account credentials, and learning history.',
    canonicalPath: '/privacy',
  },
  '/terms': {
    title: `Terms of Service & Subscriptions${TITLE_SUFFIX}`,
    description:
      'Review subscription terms, seat allocations for multiple children, billing schedules, and terms of service for Tinkerbots.',
    canonicalPath: '/terms',
  },
  '/cookies': {
    title: `Cookie Policy & Tracking Preferences${TITLE_SUFFIX}`,
    description:
      'Learn how secure session cookies and performance analytics help Tinkerbots maintain premium kid-safe learning states.',
    canonicalPath: '/cookies',
  },
  '/safeguarding': {
    title: `Child Safety & AI Safeguarding${TITLE_SUFFIX}`,
    description:
      'Discover the Tinkerbots safety-first approach: kid-safe AI tutor moderation, advanced chat filtering, and real-time parent dashboard controls.',
    canonicalPath: '/safeguarding',
  },
};

const noIndexRoutes: Record<string, SeoConfig> = {
  '/login': {
    title: `Sign In${TITLE_SUFFIX}`,
    description: 'Sign in to Tinkerbots to manage child learning profiles and continue lessons.',
  },
  '/verify-email': {
    title: `Verify Email${TITLE_SUFFIX}`,
    description: 'Verify your Tinkerbots parent account email address.',
  },
  '/404': {
    title: `Page Not Found${TITLE_SUFFIX}`,
    description: 'The Tinkerbots page you requested could not be found.',
  },
};

function getRouteSeo(pathname: string): SeoConfig {
  if (publicRouteSeo[pathname]) return publicRouteSeo[pathname];
  if (noIndexRoutes[pathname]) {
    return { ...noIndexRoutes[pathname], canonicalPath: pathname, robots: 'noindex, nofollow' };
  }

  return {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonicalPath: pathname,
    robots: 'noindex, nofollow',
  };
}

function getSiteUrl(): string {
  return (import.meta.env.VITE_PUBLIC_SITE_URL || DEFAULT_SITE_URL).replace(/\/$/, '');
}

function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${getSiteUrl()}${path}`;
}

function setMeta(selector: string, attr: 'name' | 'property', key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attr, key);
    document.head.appendChild(element);
  }
  element.content = content;
}

function setLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }
  element.href = href;
}

function setJsonLd(data?: Record<string, unknown>) {
  const id = 'route-json-ld';
  const existing = document.getElementById(id);

  if (!data) {
    existing?.remove();
    return;
  }

  const script = (existing ?? document.createElement('script')) as HTMLScriptElement;
  script.id = id;
  script.type = 'application/ld+json';
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}

export function SEO() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getRouteSeo(pathname);
    const canonicalPath = seo.canonicalPath ?? pathname;
    const canonicalUrl = absoluteUrl(canonicalPath);
    const imageUrl = absoluteUrl(seo.image ?? DEFAULT_IMAGE);
    const robots = seo.robots ?? 'index, follow';

    document.title = seo.title;
    setMeta('meta[name="description"]', 'name', 'description', seo.description);
    setMeta('meta[name="robots"]', 'name', 'robots', robots);
    setMeta('meta[property="og:site_name"]', 'property', 'og:site_name', SITE_NAME);
    setMeta('meta[property="og:type"]', 'property', 'og:type', 'website');
    setMeta('meta[property="og:url"]', 'property', 'og:url', canonicalUrl);
    setMeta('meta[property="og:title"]', 'property', 'og:title', seo.title);
    setMeta('meta[property="og:description"]', 'property', 'og:description', seo.description);
    setMeta('meta[property="og:image"]', 'property', 'og:image', imageUrl);
    setMeta('meta[name="twitter:card"]', 'name', 'twitter:card', 'summary');
    setMeta('meta[name="twitter:title"]', 'name', 'twitter:title', seo.title);
    setMeta('meta[name="twitter:description"]', 'name', 'twitter:description', seo.description);
    setMeta('meta[name="twitter:image"]', 'name', 'twitter:image', imageUrl);
    setLink('canonical', canonicalUrl);
    setJsonLd(seo.jsonLd);
  }, [pathname]);

  return null;
}
